export const THEME_STORAGE_KEY = 'themePreference';

export const AVAILABLE_THEMES = ['light', 'dark', 'synthwave'];

const LIGHT_CHART_PALETTE = [
  '#9B5DE5',
  '#F15BB5',
  '#FEE440',
  '#00BBF9',
  '#00F5D4',
  '#FB5607',
  '#3A86FF',
  '#8338EC',
  '#FF006E',
  '#2A9D8F',
  '#6A994E',
];

const DARK_CHART_PALETTE = [
  '#5eead4',
  '#f59e0b',
  '#f472b6',
  '#22c55e',
  '#818cf8',
  '#fb7185',
  '#38bdf8',
  '#facc15',
  '#a78bfa',
  '#34d399',
];

const SYNTHWAVE_CHART_PALETTE = [
  '#e779c1',
  '#58c7f3',
  '#f3cc30',
  '#c084fc',
  '#fb7185',
  '#2dd4bf',
  '#f97316',
  '#a3e635',
  '#f9a8d4',
  '#38bdf8',
];

const createThemeConfig = ({
  mode,
  primary,
  secondary,
  backgroundDefault,
  backgroundPaper,
  textPrimary,
  textSecondary,
  divider,
  ...customTokens
}) => ({
  palette: {
    mode,
    primary: { main: primary },
    secondary: { main: secondary },
    background: { default: backgroundDefault, paper: backgroundPaper },
    text: { primary: textPrimary, secondary: textSecondary },
    divider,
  },
  customTokens,
});

const THEME_CONFIG = {
  light: createThemeConfig({
    mode: 'light',
    primary: '#4361ee',
    secondary: '#2563eb',
    backgroundDefault: '#f4f6f8',
    backgroundPaper: '#ffffff',
    textPrimary: '#0f172a',
    textSecondary: '#475569',
    divider: '#d7dee7',
    appBg: '#f4f6f8',
    appText: '#0f172a',
    mutedText: '#475569',
    surfaceBg: '#ffffff',
    surfaceText: '#0f172a',
    surfaceBorder: '#d7dee7',
    menuBg: '#2f3a4a',
    menuHoverBg: '#45556b',
    menuText: '#ffffff',
    logoBg: '#ffffff',
    accent: '#4361ee',
    accentHover: '#115e59',
    fabBg: '#4361ee',
    fabBgHover: '#20338bff',
    chartGrid: 'rgba(148, 163, 184, 0.32)',
    chartTick: '#475569',
    chartTooltipBg: 'rgba(255, 255, 255, 0.96)',
    chartTooltipText: '#0f172a',
    chartPalette: LIGHT_CHART_PALETTE,
    lineColor: '#2563eb',
    lineFill: 'rgba(37, 99, 235, 0.16)',
  }),
  dark: createThemeConfig({
    mode: 'dark',
    primary: '#5eead4',
    secondary: '#7dd3fc',
    backgroundDefault: '#1d232a',
    backgroundPaper: '#191e24',
    textPrimary: '#f8fafc',
    textSecondary: '#cbd5e1',
    divider: '#334155',
    appBg: '#1d232a',
    appText: '#f8fafc',
    mutedText: '#cbd5e1',
    surfaceBg: '#191e24',
    surfaceText: '#f8fafc',
    surfaceBorder: '#334155',
    menuBg: '#2a323c',
    menuHoverBg: '#3b4754',
    menuText: '#f8fafc',
    logoBg: '#f8fafc',
    accent: '#7dd3fc',
    accentHover: '#38bdf8',
    fabBg: '#0f766e',
    fabBgHover: '#14b8a6',
    chartGrid: 'rgba(148, 163, 184, 0.24)',
    chartTick: '#cbd5e1',
    chartTooltipBg: 'rgba(15, 23, 42, 0.94)',
    chartTooltipText: '#f8fafc',
    chartPalette: DARK_CHART_PALETTE,
    lineColor: '#7dd3fc',
    lineFill: 'rgba(125, 211, 252, 0.2)',
  }),
  synthwave: createThemeConfig({
    mode: 'dark',
    primary: '#e779c1',
    secondary: '#58c7f3',
    backgroundDefault: '#2d1b69',
    backgroundPaper: '#241b5f',
    textPrimary: '#fdfd96',
    textSecondary: '#f9a8d4',
    divider: '#6d4ac7',
    appBg: '#2d1b69',
    appText: '#fdfd96',
    mutedText: '#f9a8d4',
    surfaceBg: '#241b5f',
    surfaceText: '#f3cc30',
    surfaceBorder: '#6d4ac7',
    menuBg: '#20134e',
    menuHoverBg: '#2d1b69',
    menuText: '#e779c1',
    logoBg: '#fef3c7',
    accent: '#58c7f3',
    accentHover: '#7dd3fc',
    fabBg: '#e779c1',
    fabBgHover: '#f472b6',
    chartGrid: 'rgba(231, 121, 193, 0.28)',
    chartTick: '#fdfd96',
    chartTooltipBg: 'rgba(36, 27, 95, 0.96)',
    chartTooltipText: '#fdfd96',
    chartPalette: SYNTHWAVE_CHART_PALETTE,
    lineColor: '#58c7f3',
    lineFill: 'rgba(88, 199, 243, 0.2)',
  }),
};

export const normalizeTheme = (themeName) => (
  AVAILABLE_THEMES.includes(themeName) ? themeName : 'light'
);

const CSS_VARIABLE_MAP = {
  appBg: '--app-bg',
  appText: '--app-text',
  mutedText: '--muted-text',
  surfaceBg: '--surface-bg',
  surfaceText: '--surface-text',
  surfaceBorder: '--surface-border',
  menuBg: '--menu-bg',
  menuHoverBg: '--menu-hover-bg',
  menuText: '--menu-text',
  logoBg: '--logo-bg',
  accent: '--accent',
  accentHover: '--accent-hover',
  fabBg: '--fab-bg',
  fabBgHover: '--fab-bg-hover',
  chartGrid: '--chart-grid',
  chartTick: '--chart-tick',
  chartTooltipBg: '--chart-tooltip-bg',
  chartTooltipText: '--chart-tooltip-text',
};

export const getThemeCssVariables = (themeName) => {
  const selectedTheme = THEME_CONFIG[themeName] || THEME_CONFIG.light;

  return Object.entries(CSS_VARIABLE_MAP).reduce((cssVariables, [tokenName, cssVariableName]) => {
    const tokenValue = selectedTheme.customTokens[tokenName];

    if (tokenValue) {
      cssVariables[cssVariableName] = tokenValue;
    }

    return cssVariables;
  }, {});
};

export const getMuiThemeOptions = (themeName) => {
  const selectedTheme = THEME_CONFIG[themeName] || THEME_CONFIG.light;

  return {
    ...selectedTheme,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: selectedTheme.palette.background.default,
            color: selectedTheme.palette.text.primary,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
    },
  };
};
